import * as React from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { Box, Typography, Link, Alert } from "@mui/material";
import { toast } from "react-toastify";
import AuthLayout from "../../components/AuthLayout";
import EmailStepForm from "../../components/EmailStepForm";
import OtpBoxes from "../../components/OtpBoxes";
import RegisterDetailsForm from "../../components/RegisterDetailsForm";
import AuthPrimaryButton from "../../components/AuthPrimaryButton";
import { registerStart, registerVerifyOtp, registerComplete } from "../../Services/authService";
import { uploadTherapistApplyDocument, submitTherapistApply } from "../../Services/therapistApplyService";
import { useAuth } from "../../authContext/authContext";
import { AUTH_TEXT_PRIMARY, AUTH_TEXT_SECONDARY, AUTH_ERROR, AUTH_SUCCESS, BRAND_BLUE } from "../../theme/appTheme";
import { useTranslation } from "../../i18n/LanguageContext";

const isExpiredOtpError = (err) => /expired|invalid.*(code|otp)/i.test(err?.message || "");

// Registration: email -> enter the 6-digit code from the email -> details
// (password, consent, optional "register as therapist").
function Register() {
  const navigate = useNavigate();
  const { applySession } = useAuth();
  const { t, language } = useTranslation();
  // Prefilled when coming from the login page's "please register first"
  // message (/register?email=...).
  const [searchParams] = useSearchParams();
  const prefillEmail = searchParams.get("email") || "";

  const [step, setStep] = React.useState("email"); // email | accountExists | otp | details
  const [email, setEmail] = React.useState("");
  const [canApplyAsTherapist, setCanApplyAsTherapist] = React.useState(false);
  const [otp, setOtp] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [fieldErrors, setFieldErrors] = React.useState([]);
  const [resent, setResent] = React.useState(false);

  const resetToStart = () => {
    setStep("email");
    setEmail("");
    setOtp("");
    setError("");
  };

  const startFor = async (submittedEmail) => {
    const data = await registerStart(submittedEmail, language);
    setEmail(submittedEmail);
    if (data.accountExists) {
      setCanApplyAsTherapist(Boolean(data.canApplyAsTherapist));
      setStep("accountExists");
    } else {
      setStep("otp");
    }
  };

  const handleStart = async (submittedEmail) => {
    setLoading(true);
    setError("");
    try {
      await startFor(submittedEmail);
    } catch (err) {
      if (err.status === 429) {
        setError(t("auth.register.errors.tooMany"));
      } else {
        setError(err.message || t("auth.register.errors.generic"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    setError("");
    try {
      await registerStart(email, language);
      setOtp("");
      setResent(true);
      setTimeout(() => setResent(false), 3000);
    } catch (err) {
      setError(err.message || t("auth.register.otp.resendError"));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!/^\d{6}$/.test(otp)) return;
    setLoading(true);
    setError("");
    try {
      await registerVerifyOtp(email, otp);
      setStep("details");
    } catch (err) {
      if (err.status === 429) {
        setError(t("auth.register.otp.tooMany"));
      } else {
        setError(t("auth.register.otp.incorrectCode"));
      }
    } finally {
      setLoading(false);
    }
  };

  // Runs after the account is created; failures here don't undo the
  // already-created patient account — they surface as a notice and the user
  // still lands on the dashboard, able to retry from Account Settings.
  const submitTherapistApplication = async (therapistFields) => {
    try {
      const uploaded = [];
      for (const file of therapistFields.files) {
        uploaded.push(await uploadTherapistApplyDocument(file));
      }
      await submitTherapistApply({
        practiceName: therapistFields.practiceName.trim(),
        practiceCity: therapistFields.practiceCity.trim(),
        practiceEmail: therapistFields.practiceEmail.trim(),
        documents: uploaded,
      });
      return null;
    } catch (err) {
      return err.message || t("auth.register.errors.generic");
    }
  };

  const handleCompleteRegistration = async ({
    password,
    name,
    countryId,
    languageId,
    consentPortalUpdates,
    consentMarketing,
    asTherapist,
    therapistFields,
  }) => {
    setLoading(true);
    setError("");
    setFieldErrors([]);
    try {
      const data = await registerComplete({
        email,
        otp,
        password,
        name,
        countryId,
        languageId,
        consentPortalUpdates,
        consentMarketing,
      });
      applySession(data);

      if (asTherapist && therapistFields) {
        const applyError = await submitTherapistApplication(therapistFields);
        if (applyError) {
          const reason = applyError.replace(/\.+$/, "");
          toast.error(t("auth.register.therapistApplyFailure", { reason }), { autoClose: false });
        }
      }

      navigate("/dashboard");
    } catch (err) {
      if (err.status === 400 && Array.isArray(err.errors) && err.errors.length) {
        setFieldErrors(err.errors);
      } else if (err.status === 400 && isExpiredOtpError(err)) {
        setStep("otp");
        setOtp("");
        setError(t("auth.register.completeErrors.expiredCode"));
      } else if (err.status === 409) {
        setError(t("auth.register.completeErrors.accountExists"));
      } else if (err.status === 429) {
        setError(t("auth.register.completeErrors.tooMany"));
      } else {
        setError(err.message || t("auth.register.completeErrors.generic"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      {step === "email" && (
        <EmailStepForm
          title={t("auth.register.emailStep.title")}
          subtitle={t("auth.register.emailStep.subtitle")}
          submitLabel={t("auth.register.emailStep.submit")}
          onSubmit={handleStart}
          loading={loading}
          error={error}
          initialEmail={prefillEmail}
          footer={
            <Typography sx={{ fontSize: "0.8125rem", color: AUTH_TEXT_SECONDARY }}>
              {t("auth.register.alreadyHaveAccount")}{" "}
              <Link component={RouterLink} to="/login" sx={{ color: BRAND_BLUE }}>
                {t("auth.register.signIn")}
              </Link>
            </Typography>
          }
        />
      )}

      {step === "accountExists" && (
        <Box>
          <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: "1.0625rem", color: AUTH_TEXT_PRIMARY, textAlign: "center" }}>
            {t("auth.register.accountExists.title")}
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            {canApplyAsTherapist
              ? t("auth.register.accountExists.withTherapist")
              : t("auth.register.accountExists.withoutTherapist")}
          </Alert>

          <AuthPrimaryButton onClick={() => navigate("/login")} sx={{ mt: 3 }}>
            {t("auth.register.accountExists.goToLogin")}
          </AuthPrimaryButton>

          <Typography sx={{ mt: 2.5, textAlign: "center", fontSize: "0.75rem", color: AUTH_TEXT_SECONDARY }}>
            {t("auth.register.accountExists.wrongEmail")}{" "}
            <Link component="button" type="button" onClick={resetToStart} sx={{ color: BRAND_BLUE, fontSize: "0.75rem" }}>
              {t("auth.register.accountExists.tryDifferent")}
            </Link>
          </Typography>
        </Box>
      )}

      {step === "otp" && (
        <Box>
          <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: "1.0625rem", color: AUTH_TEXT_PRIMARY, textAlign: "center" }}>
            {t("auth.register.otp.title")}
          </Typography>
          <Typography sx={{ mb: 3, fontSize: "0.8125rem", color: AUTH_TEXT_SECONDARY, textAlign: "center" }}>
            {t("auth.register.otp.subtitle").split("{{email}}")[0]}
            <strong>{email}</strong>
            {t("auth.register.otp.subtitle").split("{{email}}")[1]}
          </Typography>

          <OtpBoxes value={otp} onChange={setOtp} error={!!error} />

          {error && (
            <Typography variant="caption" sx={{ color: AUTH_ERROR, mt: 2, display: "block", textAlign: "center" }}>
              {error}
            </Typography>
          )}
          {resent && !error && (
            <Typography variant="caption" sx={{ color: AUTH_SUCCESS, mt: 2, display: "block", textAlign: "center" }}>
              {t("auth.register.otp.resent")}
            </Typography>
          )}

          <AuthPrimaryButton onClick={handleVerifyOtp} disabled={loading || !/^\d{6}$/.test(otp)} sx={{ mt: 3 }}>
            {loading ? t("auth.register.otp.checking") : t("auth.register.otp.verify")}
          </AuthPrimaryButton>

          <Alert severity="info" icon={false} sx={{ mt: 2.5, fontSize: "0.75rem", py: 0.25, justifyContent: "center", textAlign: "center" }}>
            {t("auth.emailHint")}
          </Alert>

          <Typography sx={{ mt: 2.5, textAlign: "center", fontSize: "0.75rem", color: AUTH_TEXT_SECONDARY }}>
            {t("auth.register.otp.notSeeing")}{" "}
            <Link component="button" type="button" onClick={handleResend} sx={{ color: BRAND_BLUE, fontSize: "0.75rem" }}>
              {t("auth.register.otp.sendNewCode")}
            </Link>
            .{" "}
            {t("auth.register.otp.wrongEmail")}{" "}
            <Link component="button" type="button" onClick={resetToStart} sx={{ color: BRAND_BLUE, fontSize: "0.75rem" }}>
              {t("auth.register.otp.changeEmail")}
            </Link>
            .
          </Typography>
        </Box>
      )}

      {step === "details" && (
        <RegisterDetailsForm
          email={email}
          onSubmit={handleCompleteRegistration}
          loading={loading}
          error={error}
          fieldErrors={fieldErrors}
        />
      )}
    </AuthLayout>
  );
}

export default Register;
